import React from 'react';
import { View } from '../../types/types';
import { handleActionError } from '../../utils/engineering-tools';
import { TimkenSortDirection } from '../../../components/bearing-search/types/types';

export type ProductSearchState = {
	currentPage: number;
	totalPages: number;
	pageSize: number;
	isRefining: boolean;
	sortExpression?: string;
	sortDirection?: TimkenSortDirection;
	view: View;
	columns: string[];
	filters: string[];
	searchData: string[][];
	filteredData: string[][];
	displayedData: string[][];
	partNumber: string;
};

export const initialSearchState: ProductSearchState = {
	currentPage: 1,
	totalPages: 0,
	pageSize: 6,
	isRefining: false,
	view: View.LIST,
	columns: [],
	filters: [],
	searchData: [],
	filteredData: [],
	displayedData: [],
	partNumber: "",
};

export type ProductSearchAction =
	| { type: 'update_data'; payload: [string[][], boolean]; }
	| { type: 'update_columns'; payload: string[]; }
	| { type: 'change_page'; payload: number; }
	| { type: 'sort_by'; payload: string; }
	| { type: 'open_modal'; payload: string; }
	| { type: 'filter_by'; payload: [number, string]; }
	| { type: 'change_view'; payload: View; }
	| { type: 'reset'; payload?: ProductSearchState; };

const paginate = ( data: string[][], size: number, current: number ): [string[][], number] => {
	const displayed = data.slice(
		( current - 1 ) * size,
		Math.min( current * size, data.length ),
	);

	const pages = data.length === 0 ? 0 : Math.ceil( data.length / size );

	return [ displayed, pages ];
};

const filter = ( data: string[][], filters: string[] ): string[][] => {
	return data.filter( ( item: string[] ) => {
		for ( let col = 0; col < filters.length; col++ ) {
			const keyword = filters[ col ];

			if ( ! keyword ) {
				continue;
			}

			const searchRegex = new RegExp( keyword.replace( /[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&' ), 'i' );

			if ( ! searchRegex.exec( item[ col ] ) ) {
				return false;
			}
		}

		return true;
	} );
};

const productSearchReducer: React.Reducer<
	ProductSearchState,
	ProductSearchAction
> = ( state, action ) => {
	switch ( action.type ) {
		case 'update_data': {
			const [ data, refining ] = action.payload;

			const newState: ProductSearchState = {
				...state,
				searchData: data,
				isRefining: false,
			};

			// filter
			if ( ! refining ) {
				newState.filters = Array( state.columns.length ).fill( '' );
				newState.sortExpression = undefined;
				newState.sortDirection = undefined;
			}
			const filteredData = filter( data, newState.filters );

			// paginate
			const [ displayedData, totalPages ] = paginate( filteredData, state.pageSize, state.currentPage );

			newState.filteredData = filteredData;
			newState.displayedData = displayedData;
			newState.totalPages = totalPages;

			return newState;
		}
		case 'update_columns': {
			const columns = action.payload;
			const filters = Array( columns.length ).fill( '' );

			return {
				...state,
				columns,
				filters,
			};
		}
		case 'change_page': {
			const currentPage = action.payload;
			const [ displayedData, totalPages ] = paginate( state.filteredData, state.pageSize, currentPage );

			return {
				...state,
				currentPage,
				displayedData,
				totalPages,
			};
		}

		case 'open_modal': {
			return {
				...state,
				partNumber: action.payload
			}
		}

		case 'sort_by': {
			const { sortDirection, sortExpression } = state;
			const newSortExpression = action.payload;

			if ( ! newSortExpression ) {
				// Unsort
				return {
					...state,
					sortKey: undefined,
					sortDirection: undefined,
				};
			}

			const newSortDirection = sortExpression !== newSortExpression || sortDirection !== 'asc' ? 'asc' : 'desc';

			return {
				...state,
				isRefining: true,
				sortExpression: newSortExpression,
				sortDirection: newSortDirection,
			};
		}
		case 'change_view': {
			return {
				...state,
				view: action.payload,
			};
		}
		case 'filter_by': {
			const [ columnIndex, keyword ] = action.payload;
			const filters = [ ...state.filters ];
			filters[ columnIndex ] = keyword;
			const filteredData = filter( state.searchData, filters );
			const currentPage = initialSearchState.currentPage;
			const [ displayedData, totalPages ] = paginate( filteredData, state.pageSize, currentPage );

			return {
				...state,
				filters,
				filteredData,
				displayedData,
				totalPages,
				currentPage,
			};
		}
		case 'reset': {
			const newState = action.payload ? action.payload : initialSearchState;

			return {
				...newState,
			};
		}
	}

	return handleActionError( action );
};

export default productSearchReducer;
